import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Animated,
	Image,
	ImageBackground,
	ImageStyle,
	Modal,
	StyleSheet,
	TextStyle,
	View,
	ViewStyle,
} from 'react-native';
import Toast from 'react-native-toast-message';

import Portal from '../../../shared/Portal';
import { SUPPORTED_MODAL_ORIENTATIONS } from '../../../store/types';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { KeyboardLift } from '../../../utils/keyboard/KeyboardAvoidance';
import { getInsetAdjustedOffset, getSafeAreaSidePadding } from '../../../utils/safeArea';
import { globalBroadcastToastConfig } from '../../navigation/globalBroadcastToastConfig';
import { TouchableOpacity } from '../common/Touchable';
import {
	RedesignCloseButton,
	redesignPanelColors,
	RedesignPanelFill,
	redesignPanelFrame,
} from './redesignPanelChrome';

const PANEL_RADIUS = 16;

type SvaraModalProps = {
	visible: boolean;
	onClose: () => void;
	titleImage?: any;
	backgroundImage?: any;
	children?: React.ReactNode;
	/**
	 * @deprecated The panel now always draws the redesign's round close button in its top-right
	 * corner; these old close-button props are ignored.
	 */
	closeIcon?: any;
	/** @deprecated See `closeIcon`. */
	closeButtonSize?: 'small' | 'big';
	/** @deprecated See `closeIcon`. */
	bigCloseButton?: boolean;
	/** @deprecated See `closeIcon`. */
	bigCloseBtnStyles?: ViewStyle;
	/** @deprecated See `closeIcon`. */
	closeButtonStyles?: ViewStyle;
	containerStyles?: ViewStyle;
	contentWrapperStyles?: ViewStyle;
	titleImageStyle?: ImageStyle;
	overlayStyle?: ViewStyle;
	respectHorizontalSafeArea?: boolean;
	keepMountedForMsOnClose?: number;
	stretchContent?: boolean;
};

const SvaraModal = ({
	visible,
	onClose,
	titleImage,
	backgroundImage,
	titleImageStyle,
	children,
	containerStyles,
	contentWrapperStyles,
	overlayStyle,
	respectHorizontalSafeArea = true,
	keepMountedForMsOnClose = 0,
	stretchContent = false,
}: SvaraModalProps) => {
	const { t } = useTranslation();
	const opacity = useRef(new Animated.Value(0)).current;
	const panelRef = useRef<View>(null);
	const { insets } = useSafeViewport();
	const { isWeb } = useDevice();
	const toastBottomOffset = getInsetAdjustedOffset(insets.bottom, 20);
	const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [isMountedAfterClose, setIsMountedAfterClose] = useState(visible);

	useEffect(() => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}

		if (visible) {
			setIsMountedAfterClose(true);
			return;
		}

		if (keepMountedForMsOnClose > 0) {
			closeTimeoutRef.current = setTimeout(() => {
				setIsMountedAfterClose(false);
				closeTimeoutRef.current = null;
			}, keepMountedForMsOnClose);
		} else {
			setIsMountedAfterClose(false);
		}

		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
				closeTimeoutRef.current = null;
			}
		};
	}, [keepMountedForMsOnClose, visible]);

	const safeOverlayInsets = respectHorizontalSafeArea ? getSafeAreaSidePadding(insets) : null;

	useEffect(() => {
		if (visible) {
			if (isWeb) {
				opacity.setValue(1);
				return;
			}

			Animated.timing(opacity, {
				toValue: 1,
				duration: 200,
				useNativeDriver: true,
			}).start();
		}
	}, [visible, opacity, isWeb]);

	const handleClose = () => {
		if (isWeb) {
			opacity.setValue(0);
			onClose();
			return;
		}

		Animated.timing(opacity, {
			toValue: 0,
			duration: 200,
			useNativeDriver: true,
		}).start(onClose);
	};

	const finalWidth = (titleImageStyle as any)?.width ?? styles.titleImage.width ?? 340;

	if (!visible && !isMountedAfterClose) {
		return null;
	}

	const modalBody = (
		<Animated.View
			style={[
				styles.overlay,
				!visible && styles.hiddenOverlay,
				overlayStyle,
				safeOverlayInsets,
				{ opacity },
				isWeb && styles.webOverlay,
			]}
		>
			{visible && (
				<TouchableOpacity activeOpacity={1} onPress={handleClose} style={styles.backdropTapArea} />
			)}
			{visible && (
				// The panel rises over the keyboard to keep a focused field in it in sight.
				<KeyboardLift
					style={StyleSheet.absoluteFill}
					contentStyle={[styles.liftContent, safeOverlayInsets]}
					boundsRef={panelRef}
				>
					{backgroundImage ? (
						<ImageBackground
							source={backgroundImage}
							resizeMode='stretch'
							style={[styles.modalBackground, styles.imagePanel, containerStyles]}
						>
							<View
								ref={panelRef}
								style={[styles.modalContainer, stretchContent && styles.modalContainerStretch]}
							>
								{titleImage && (
									<Image
										source={titleImage}
										resizeMode='contain'
										style={[
											styles.titleImage,
											titleImageStyle,
											{
												transform: [{ translateX: -(Number(finalWidth) || 0) / 2 }],
											},
										]}
									/>
								)}

								<RedesignCloseButton
									onPress={handleClose}
									accessibilityLabel={t('T_SITE_CLOSE', { defaultValue: 'Close' })}
								/>

								<View
									style={[
										styles.content,
										stretchContent && styles.contentStretch,
										contentWrapperStyles,
									]}
								>
									{children}
								</View>
							</View>
						</ImageBackground>
					) : (
						<View style={[styles.modalBackground, styles.redesignPanel, containerStyles]}>
							<RedesignPanelFill radius={PANEL_RADIUS} />
							<View
								ref={panelRef}
								style={[styles.modalContainer, stretchContent && styles.modalContainerStretch]}
							>
								{titleImage && (
									<Image
										source={titleImage}
										resizeMode='contain'
										style={[
											styles.titleImage,
											titleImageStyle,
											{
												transform: [{ translateX: -(Number(finalWidth) || 0) / 2 }],
											},
										]}
									/>
								)}

								<RedesignCloseButton
									onPress={handleClose}
									accessibilityLabel={t('T_SITE_CLOSE', { defaultValue: 'Close' })}
								/>

								<View
									style={[
										styles.content,
										stretchContent && styles.contentStretch,
										contentWrapperStyles,
									]}
								>
									{children}
								</View>
							</View>
						</View>
					)}
				</KeyboardLift>
			)}
		</Animated.View>
	);

	// Web: render into a portal at document.body so CSS transforms on carousel/parent
	// containers do not break position:fixed stacking.
	if (isWeb) {
		return (
			<Portal>
				<View style={styles.webModalRoot} pointerEvents='box-none'>
					{modalBody}
					<View pointerEvents='box-none' style={styles.toastOverlay}>
						<Toast
							config={globalBroadcastToastConfig}
							position='bottom'
							bottomOffset={toastBottomOffset}
						/>
					</View>
				</View>
			</Portal>
		);
	}

	return (
		<Modal
			animationType='none'
			transparent
			visible={visible}
			presentationStyle='overFullScreen'
			supportedOrientations={SUPPORTED_MODAL_ORIENTATIONS}
			statusBarTranslucent
			navigationBarTranslucent
			onRequestClose={handleClose}
		>
			{modalBody}
			{isWeb ? (
				<View pointerEvents='box-none' style={styles.toastOverlay}>
					<Toast
						config={globalBroadcastToastConfig}
						position='bottom'
						bottomOffset={toastBottomOffset}
					/>
				</View>
			) : null}
		</Modal>
	);
};

const styles = StyleSheet.create<{
	overlay: ViewStyle;
	hiddenOverlay: ViewStyle;
	webOverlay: ViewStyle;
	webModalRoot: ViewStyle;
	toastOverlay: ViewStyle;
	backdropTapArea: ViewStyle;
	liftContent: ViewStyle;
	modalBackground: ViewStyle;
	modalContainer: ViewStyle;
	modalContainerStretch: ViewStyle;
	titleImage: ImageStyle;
	imagePanel: ViewStyle;
	redesignPanel: ViewStyle;
	content: ViewStyle;
	contentStretch: ViewStyle;
	modalTitle: TextStyle;
	connectText: TextStyle;
}>({
	overlay: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: redesignPanelColors.backdrop,
	},
	hiddenOverlay: {
		backgroundColor: 'transparent',
	},
	webOverlay: {
		position: 'fixed' as any,
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 9999,
	},
	webModalRoot: {
		position: 'fixed' as any,
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 9999,
		pointerEvents: 'box-none',
	},
	backdropTapArea: {
		...StyleSheet.absoluteFill,
	},
	liftContent: {
		...StyleSheet.absoluteFill,
		justifyContent: 'center',
		alignItems: 'center',
	},
	toastOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		justifyContent: 'flex-end',
		alignItems: 'center',
		paddingBottom: 20,
		zIndex: 999999,
		elevation: 999999,
		pointerEvents: 'box-none',
	},
	modalBackground: {
		maxWidth: '96%',
		maxHeight: '90%',
		padding: 2,
		paddingTop: 0,
	},
	imagePanel: {
		borderRadius: PANEL_RADIUS,
	},
	redesignPanel: redesignPanelFrame(PANEL_RADIUS),

	modalContainer: {
		width: '100%',
		maxHeight: '100%',
		position: 'relative',
		paddingTop: 0,
	},
	modalContainerStretch: {
		height: '100%',
	},
	titleImage: {
		width: 340,

		height: 120,
		position: 'absolute',
		top: -70,
		left: '50%',
	},
	content: {
		alignItems: 'center',
		paddingHorizontal: 20,
		// Clears the close button in the top-right corner.
		paddingTop: 44,
		gap: 20,
	},
	contentStretch: {
		width: '100%',
		alignSelf: 'stretch',
	},
	modalTitle: {
		color: '#fff',
		fontSize: 22,
		fontWeight: 'bold',
	},
	connectText: {
		color: '#ccc',
		fontSize: 16,
		textAlign: 'center',
	},
});

export default SvaraModal;
